import { useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, Thead, Tbody, Tr, Th, Td } from '../../components/ui/table';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { useToast } from '../../components/ui/toast';

interface Tag {
  id: string;
  name: string;
  color: string;
}

export function TagsPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: () => api.get<Tag[]>('/tags'),
  });
  const tags = data?.data ?? [];

  const [editing, setEditing] = useState<Partial<Tag> | null>(null);

  const saveMutation = useMutation({
    mutationFn: (t: Partial<Tag>) =>
      t.id ? api.patch(`/tags/${t.id}`, t) : api.post('/tags', t),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tags'] }); setEditing(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/tags/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tags'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">Tag-uri</h1>
        <Button onClick={() => setEditing({ name: '', color: '#6b7280' })}>
          <Plus size={18} /> Adaugă
        </Button>
      </div>

      <Card>
        <Table>
          <Thead>
            <Tr>
              <Th>Culoare</Th>
              <Th>Nume</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && Array.from({ length: 3 }).map((_, i) => (
              <Tr key={i}><Td colSpan={3}><Skeleton className="h-4 w-full" /></Td></Tr>
            ))}
            {tags.map((t) => (
              <Tr key={t.id}>
                <Td><span className="inline-block h-5 w-5 rounded" style={{ backgroundColor: t.color }} /></Td>
                <Td className="font-medium">{t.name}</Td>
                <Td className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(t)}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => { if (confirm('Ștergi tag-ul?')) deleteMutation.mutate(t.id); }}><Trash2 size={14} /></Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Card>

      {editing && (
        <Dialog open onClose={() => setEditing(null)} title={editing.id ? 'Editează tag' : 'Tag nou'}>
          <div className="space-y-4">
            <Input label="Nume" value={editing.name ?? ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text">Culoare</label>
              <input type="color" value={editing.color ?? '#6b7280'} onChange={(e) => setEditing({ ...editing, color: e.target.value })} className="h-10 w-20 cursor-pointer rounded border" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Anulează</Button>
              <Button onClick={() => saveMutation.mutate(editing)} disabled={saveMutation.isPending}>Salvează</Button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}
